/* eslint-disable no-restricted-syntax -- full-bleed story chrome: Stories is a
   black image/video canvas with a white text/icon overlay throughout this
   file, matching real Instagram Stories UI, not a Mora surface, so
   bg-background/text-foreground don't apply. Scope is the white/black
   overlay chrome only — no hex literal remains in this file, see
   text-platform-instagram-verified below. */
import { HeartIcon, PaperPlaneTiltIcon, SealCheckIcon, XIcon } from '@phosphor-icons/react';
import React from 'react';

import { MediaSlot } from './MediaSlot';
import { PreviewAvatar } from './PreviewAvatar';
import {
  DEFAULT_PREVIEW_BRAND_COLOR,
  type PlatformPreviewBaseProps,
  previewTimestampLabel,
} from './types';

import { cn } from '@/lib/utils';

/**
 * Instagram Story — full-bleed vertical 9:16 viewer replica (Task A5), the
 * real Story anatomy (not the Reels one — the two look similar at a glance
 * but every chrome element differs):
 *
 *   - SEGMENTED progress bars across the top (one per story in the ring —
 *     this replica shows a single-story ring since Mora posts one asset at
 *     a time), unlike Reels' single thin line at the bottom edge.
 *   - Identity header row directly BELOW the progress bars: avatar +
 *     username + relative timestamp, top-LEFT — Reels puts identity at the
 *     bottom-left instead; Stories has no floating bottom identity block.
 *   - Close (X) top-right, alongside the header row.
 *   - Bottom reply bar: a pill-shaped "Send message" text-entry affordance
 *     + heart + share (paper-plane) glyphs — Stories' interaction model is
 *     reply-first, unlike Reels' vertical like/comment/share action rail.
 *
 * Colors are hardcoded (same rule as InstagramReel/TikTokVideo/
 * SafeZoneOverlay): this chrome sits ON arbitrary media and must not invert
 * with the app theme.
 */
export const InstagramStory: React.FC<PlatformPreviewBaseProps> = ({
  brandName,
  username,
  brandColor = DEFAULT_PREVIEW_BRAND_COLOR,
  avatarUrl,
  isVerified = false,
  imageUrl,
  videoUrl,
  videoPoster,
  focalX,
  focalY,
  zoom,
  timestamp,
  isLoading = false,
  mediaOverlay,
  className,
}) => {
  const handle = (username || brandName).toLowerCase().replace(/\s+/g, '');

  return (
    <article
      className={cn('relative w-full aspect-[9/16] rounded-xl overflow-hidden bg-black', className)}
      data-platform="instagram-story"
    >
      <MediaSlot
        fill
        bg="zinc"
        videoFirst
        imageUrl={imageUrl}
        videoUrl={videoUrl}
        videoPoster={videoPoster}
        focalX={focalX}
        focalY={focalY}
        zoom={zoom}
        isLoading={isLoading}
        alt={`${brandName} story`}
        overlay={mediaOverlay}
      />

      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/50 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/45 to-transparent pointer-events-none" />

      {/* Segmented progress — a single segment, because Mora previews one
          story asset at a time.

          🔴 EMPTY, and it must stay empty. The comment here used to say the
          bar was "mostly-filled to read as 'playing' rather than 'just
          started'" — which is an admission that the value was chosen for how
          it LOOKS, not measured from anything. A story that has never been
          published has no playback position, and this directory's rule is
          that a preview never states something it was not given. The paid
          `StoryAdPreview` pins its own bar at zero for the same reason. If a
          real position ever exists, take it as a prop. */}
      <div className="absolute inset-x-2.5 top-2 flex items-center gap-1" aria-hidden>
        <span className="h-0.5 flex-1 rounded-full bg-white/35 overflow-hidden">
          <span className="block h-full w-0 rounded-full bg-white" />
        </span>
      </div>

      <div className="absolute inset-x-2.5 top-5 flex items-center gap-2 text-white">
        <PreviewAvatar
          brandName={brandName}
          brandColor={brandColor}
          avatarUrl={avatarUrl}
          className="size-7 rounded-full border border-white/50 shrink-0"
          textClassName="text-micro font-semibold"
        />
        <p className="text-detail font-semibold truncate">{handle}</p>
        {isVerified ? (
          <SealCheckIcon
            className="size-3.5 shrink-0 text-platform-instagram-verified"
            weight="fill"
            aria-label="Verified"
          />
        ) : null}
        <span className="text-detail text-white/70 shrink-0">
          {previewTimestampLabel(timestamp)}
        </span>
        <button
          type="button"
          aria-label="Close story"
          className="ml-auto inline-flex size-6 shrink-0 items-center justify-center rounded-full text-white/90"
        >
          <XIcon className="size-5" weight="bold" />
        </button>
      </div>

      <div className="absolute inset-x-2.5 bottom-3 flex items-center gap-2">
        <div className="flex-1 min-w-0 rounded-full border border-white/60 px-4 py-2">
          <span className="text-detail text-white/85 truncate block">Send message</span>
        </div>
        <button
          type="button"
          aria-label="Like"
          className="inline-flex size-9 shrink-0 items-center justify-center text-white"
        >
          <HeartIcon className="size-6" weight="regular" />
        </button>
        <button
          type="button"
          aria-label="Share"
          className="inline-flex size-9 shrink-0 items-center justify-center text-white"
        >
          <PaperPlaneTiltIcon className="size-6" weight="regular" />
        </button>
      </div>
    </article>
  );
};
